/**
 * useDesignerState — All component state for the Unified Workflow Designer.
 *
 * Extracted from UnifiedWorkflowDesigner.tsx lines 386-651.
 */

import { useApolloClient } from '@apollo/client/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { HEALTHCARE_RCM_EXAMPLES, loadRCMExamples } from '../examples/HealthcareRCMWorkflows';
import type { TemplateListItem } from '../metadata/services/MetadataTemplateService';
import { getMetadataTemplateService } from '../metadata/services/MetadataTemplateService';
import { MetadataInterpreter } from '../metadata/MetadataInterpreter';
import type { PrimitiveInstance } from '../shared/PrimitivesLibrarySidebar';
import type { PrimitiveWorkflowType } from '../shared/ReactFlowCanvas';
import type { WorkflowMetadata } from '../shared/WorkflowManagementSidebar';
import { WorkflowDraftService, generateDraftKey } from '../services/WorkflowDraftService';
import type { DesignerState, EcosystemData } from './types';
import { createEmptyEcosystemData } from './types';

// ── Local storage helpers ─────────────────────────────────────────────

const loadSavedWorkflows = (): WorkflowMetadata[] => {
  try {
    const saved = localStorage.getItem('ferp_workflows');
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

export const saveWorkflowsToStorage = (workflows: WorkflowMetadata[]) => {
  try {
    localStorage.setItem('ferp_workflows', JSON.stringify(workflows));
  } catch (error) {
    console.error('Failed to save workflows:', error);
  }
};

// ── Default primitives ────────────────────────────────────────────────

const DEFAULT_PRIMITIVES: PrimitiveInstance[] = [
  {
    id: 'actor_registration_clerk',
    type: 'ActorDescriptor',
    name: 'Registration Clerk',
    description: 'Front desk patient registration and intake',
    metadata: {
      actorType: 'human',
      roles: ['patient_access', 'data_entry'],
      permissions: ['create_patient', 'view_insurance', 'run_eligibility'],
    },
    createdAt: new Date().toISOString(),
    createdBy: 'system',
  },
  {
    id: 'actor_certified_coder',
    type: 'ActorDescriptor',
    name: 'Certified Medical Coder',
    description: 'CPC - codes diagnoses and procedures',
    metadata: {
      actorType: 'human',
      roles: ['medical_coder', 'charge_entry'],
      permissions: ['assign_codes', 'review_ai', 'submit_charges'],
    },
    createdAt: new Date().toISOString(),
    createdBy: 'system',
  },
  {
    id: 'actor_billing_specialist',
    type: 'ActorDescriptor',
    name: 'Billing Specialist',
    description: 'Claims submission and follow-up',
    metadata: {
      actorType: 'human',
      roles: ['billing', 'claims'],
      permissions: ['submit_claims', 'handle_denials'],
    },
    createdAt: new Date().toISOString(),
    createdBy: 'system',
  },
  {
    id: 'actor_ar_specialist',
    type: 'ActorDescriptor',
    name: 'AR Specialist',
    description: 'Accounts receivable and collections',
    metadata: {
      actorType: 'human',
      roles: ['collections', 'payment_posting'],
      permissions: ['post_payments', 'setup_payment_plans'],
    },
    createdAt: new Date().toISOString(),
    createdBy: 'system',
  },
  {
    id: 'actor_compliance_officer',
    type: 'ActorDescriptor',
    name: 'Compliance Officer',
    description: 'HIPAA and regulatory compliance',
    metadata: {
      actorType: 'human',
      roles: ['compliance', 'auditor'],
      permissions: ['audit_workflows', 'enforce_policies'],
    },
    createdAt: new Date().toISOString(),
    createdBy: 'system',
  },
];

// ── Hook ──────────────────────────────────────────────────────────────

export function useDesignerState(
  tenant: string,
  executiveMode: boolean,
): DesignerState {
  const apolloClient = useApolloClient() as any;

  // Workflow management
  const [savedWorkflows, setSavedWorkflows] = useState<WorkflowMetadata[]>(() => {
    const saved = loadSavedWorkflows();
    const hasExamples = saved.some((w) => w.id.startsWith('rcm_'));
    if (!hasExamples) {
      const examples = loadRCMExamples();
      saveWorkflowsToStorage([...saved, ...examples]);
      return [...saved, ...examples];
    }
    return saved;
  });
  const [currentWorkflow, setCurrentWorkflow] = useState<WorkflowMetadata | null>(null);
  const [workflowSidebarOpen, setWorkflowSidebarOpen] = useState(false);

  // Draft service
  const draftService = useMemo(
    () =>
      new WorkflowDraftService(apolloClient, tenant, {
        autosaveIntervalMs: 5000,
        draftTTLSeconds: 86400,
      }),
    [apolloClient, tenant],
  );
  const draftKey = useMemo(() => generateDraftKey(currentWorkflow?.id), [currentWorkflow?.id]);

  // Canvas state
  const [ecosystemData, setEcosystemData] = useState<EcosystemData>(() =>
    createEmptyEcosystemData(),
  );
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);
  const [activeQuadrant, setActiveQuadrant] = useState<string>('DESIGN');
  const [designerMode, setDesignerMode] = useState<'definition' | 'instance'>('definition');
  const [commandCenterOpen, setCommandCenterOpen] = useState(false);
  const [joinerOpen, setJoinerOpen] = useState(false);

  // Business templates
  const [showTemplates, setShowTemplates] = useState(true);
  const [businessTemplates, setBusinessTemplates] = useState<TemplateListItem[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateListItem | null>(null);
  const [templateFormOpen, setTemplateFormOpen] = useState(false);
  const [activeTemplateInterpreter, setActiveTemplateInterpreter] =
    useState<MetadataInterpreter | null>(null);

  // Primitives library
  const [primitiveLibrary, setPrimitiveLibrary] = useState<PrimitiveInstance[]>(
    () => DEFAULT_PRIMITIVES,
  );
  const [selectedPrimitiveType, setSelectedPrimitiveType] =
    useState<PrimitiveWorkflowType>('ActorDescriptor');
  const [libraryCollapsed, setLibraryCollapsed] = useState(false);

  // CRUD form
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formPrimitiveType, setFormPrimitiveType] = useState<PrimitiveWorkflowType | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'view'>('create');
  const [formInitialData, setFormInitialData] = useState<Record<string, any> | undefined>(
    undefined,
  );
  const [pendingDropPosition, setPendingDropPosition] = useState<{ x: number; y: number } | null>(
    null,
  );

  // Role browser (Issue #846: backend RBAC integration)
  const [roleBrowserOpen, setRoleBrowserOpen] = useState(false);

  // Draft status
  const [lastDraftSave, setLastDraftSave] = useState<Date | null>(null);
  const [draftSaving, setDraftSaving] = useState(false);

  // ── Effects ─────────────────────────────────────────────────────────

  // Load business templates
  useEffect(() => {
    const loadBusinessTemplates = async () => {
      try {
        const service = getMetadataTemplateService(tenant, apolloClient);
        const templates = await service.listTemplates();
        setBusinessTemplates(templates);
      } catch (error) {
        console.error('[UnifiedWorkflowDesigner] Failed to load business templates:', error);
      }
    };
    if (tenant) loadBusinessTemplates();
  }, [tenant, apolloClient]);

  // Load draft from Redis on mount
  useEffect(() => {
    const loadDraft = async () => {
      try {
        const draft = await draftService.getDraft(draftKey);
        if (draft && draft.data) {
          setEcosystemData({
            workflows_on_canvas: draft.data.nodes.map((node: any) => ({
              id: node.id,
              type: node.type,
              name: node.name,
              x: node.x,
              y: node.y,
              status: 'active',
              health: 100,
              connections: draft.data.edges
                .filter((edge: any) => edge.source === node.id)
                .map((edge: any) => edge.target),
              metadata: node.metadata,
            })),
            ecosystem_timeline: [],
            ecosystem_optimizations: [],
            cross_wall_relationships: [],
          });
        }
      } catch (error) {
        console.log(
          '[Draft] GraphQL backend not available, using localStorage only:',
          error instanceof Error ? error.message : String(error),
        );
      }
    };
    loadDraft();
  }, [draftKey, draftService]);

  // Auto-save to Redis every 5 seconds
  useEffect(() => {
    const actorName = executiveMode ? 'Executive User' : 'Standard User';
    const autosaveTimer = window.setInterval(async () => {
      const data = {
        nodes: ecosystemData.workflows_on_canvas.map((wf) => ({
          id: wf.id,
          type: wf.type,
          name: wf.name,
          x: wf.x,
          y: wf.y,
          metadata: wf.metadata || {},
        })),
        edges: ecosystemData.workflows_on_canvas.flatMap((wf) =>
          wf.connections.map((target) => ({
            id: `${wf.id}_${target}`,
            source: wf.id,
            target,
            metadata: {},
          })),
        ),
        workflowMetadata: currentWorkflow
          ? {
              name: currentWorkflow.name,
              description: currentWorkflow.description,
              version: currentWorkflow.version,
            }
          : undefined,
      };

      if (data.nodes.length > 0 || data.edges.length > 0) {
        setDraftSaving(true);
        try {
          await draftService.saveDraft(draftKey, data, actorName);
          setLastDraftSave(new Date());
        } catch {
          setLastDraftSave(null);
        } finally {
          setDraftSaving(false);
        }
      }
    }, 5000);

    return () => clearInterval(autosaveTimer);
  }, [draftKey, draftService, ecosystemData, currentWorkflow, executiveMode]);

  return {
    savedWorkflows, setSavedWorkflows,
    currentWorkflow, setCurrentWorkflow,
    workflowSidebarOpen, setWorkflowSidebarOpen,
    ecosystemData, setEcosystemData,
    selectedWorkflowId, setSelectedWorkflowId,
    activeQuadrant, setActiveQuadrant,
    designerMode, setDesignerMode,
    commandCenterOpen, setCommandCenterOpen,
    joinerOpen, setJoinerOpen,
    showTemplates, setShowTemplates,
    businessTemplates, setBusinessTemplates,
    selectedTemplate, setSelectedTemplate,
    templateFormOpen, setTemplateFormOpen,
    activeTemplateInterpreter, setActiveTemplateInterpreter,
    primitiveLibrary, setPrimitiveLibrary,
    selectedPrimitiveType, setSelectedPrimitiveType,
    libraryCollapsed, setLibraryCollapsed,
    formDialogOpen, setFormDialogOpen,
    formPrimitiveType, setFormPrimitiveType,
    formMode, setFormMode,
    formInitialData, setFormInitialData,
    pendingDropPosition, setPendingDropPosition,
    roleBrowserOpen, setRoleBrowserOpen,
    lastDraftSave, setLastDraftSave,
    draftSaving, setDraftSaving,
    draftKey,
  };
}
